import { Clock } from 'lucide-react';
import { formatPreferenceLabel } from '@/utils/format';
import {
  hasAnyPreference,
  type EntryWithPreferences,
} from '@/utils/entryPreferences';

interface EntryPreferencesDisplayProps {
  entry: EntryWithPreferences;
  /**
   * 'compact' (default) renders inline on the dashboard card. 'detailed'
   * renders a stacked list for the EventDetailPage entry card.
   */
  variant?: 'compact' | 'detailed';
  className?: string;
  /**
   * IANA timezone of the parent rodeo. When supplied, every preference
   * datetime is rendered in that zone with the US-style abbreviation
   * appended ("Fri, May 15, 6:00 PM PT") so the contestant sees the
   * exact wall-clock the host configured plus an explicit zone hint.
   * Omit for legacy callers without rodeo context — the helper falls
   * back to the viewer's local zone in that case.
   */
  timezone?: string | null;
}

/**
 * Inline render of a registration's selected performance preferences plus
 * the priority-resolved slot (when set + different). Returns null when no
 * preference data exists so callers can drop it in unconditionally.
 */
export function EntryPreferencesDisplay({
  entry,
  variant = 'compact',
  className,
  timezone,
}: EntryPreferencesDisplayProps) {
  if (!hasAnyPreference(entry)) return null;

  const first = formatPreferenceLabel(entry.first_performance_preference, timezone);
  const second = formatPreferenceLabel(entry.second_performance_preference, timezone);

  // The assigned slot the contestant cares about is their ACTUAL placement
  // in the finalized draw (`assigned_performance_name`) — the exact value
  // the day sheet, finalize email, and public draw-results card show. It's
  // a raw performance name, not a preference token, so it renders as-is.
  //
  // `resolved_performance_slot` is only the preference-RESOLVER's output (an
  // input to the draw) and can legitimately diverge from where the draw
  // landed them; using it as "Assigned slot" is exactly what made this card
  // disagree with every other surface. Fall back to it only pre-finalize,
  // when no real placement exists yet.
  const finalizedSlot = entry.assigned_performance_name?.trim() || null;
  const resolvedSlot = formatPreferenceLabel(entry.resolved_performance_slot, timezone);
  const assigned = finalizedSlot ?? (resolvedSlot || null);

  // Show the assigned row whenever we have an authoritative finalized
  // placement, OR (pre-finalize) when the resolver moved the contestant off
  // their 1st pick — keeping it hidden when the resolved slot just echoes
  // the 1st preference so the pre-draw copy stays quiet.
  const resolvedDiffers =
    !!finalizedSlot ||
    (!!resolvedSlot && resolvedSlot.toLowerCase() !== (first ?? '').toLowerCase());

  if (variant === 'compact') {
    return (
      <div
        className={
          'flex items-center gap-1 text-xs text-muted-foreground ' +
          (className ?? '')
        }
      >
        <Clock className="h-3 w-3 shrink-0" />
        <span className="truncate">
          <span className="font-medium text-foreground/80">Preferences:</span>{' '}
          {first || '—'}
          {second ? ` → ${second}` : ''}
          {resolvedDiffers && assigned ? (
            <span className="ml-1 text-foreground/60">(assigned: {assigned})</span>
          ) : null}
        </span>
      </div>
    );
  }

  return (
    <div className={'rounded-lg bg-muted/40 p-3 text-xs space-y-1 ' + (className ?? '')}>
      <div className="flex items-center gap-1.5 font-medium text-foreground/80">
        <Clock className="h-3.5 w-3.5" />
        Performance preferences
      </div>
      <div className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-0.5">
        <span className="text-muted-foreground">1st choice</span>
        <span>{first || '—'}</span>
        {second && (
          <>
            <span className="text-muted-foreground">2nd choice</span>
            <span>{second}</span>
          </>
        )}
        {resolvedDiffers && assigned && (
          <>
            <span className="text-muted-foreground">Assigned slot</span>
            <span className="font-medium">{assigned}</span>
          </>
        )}
      </div>
    </div>
  );
}
